import { useState, useEffect, useCallback } from 'react';
import {
  Settings2,
  Loader2,
  AlertCircle,
  Plus,
  X,
  Trash2,
  CheckCircle2,
  ToggleLeft,
  ToggleRight,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';
import { periodDisplayName, type Period, type ConsolidationRule } from '@/types';
import { periodsApi, rulesApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Constants ---------------------------------------------------------------

const CONDITION_FIELDS = [
  'entity_type',
  'account_type',
  'amount',
  'currency',
  'period_type',
  'ownership_pct',
  'consolidation_method',
  'is_intercompany',
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '!=' },
  { value: 'greater_than', label: '>' },
  { value: 'less_than', label: '<' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in' },
];

const ACTION_TYPES = [
  'eliminate_balance',
  'create_journal_entry',
  'flag_for_review',
  'apply_fx_rate',
  'reclassify_account',
  'adjust_nci',
];

// --- Create/Edit Modal -------------------------------------------------------

interface RuleModalProps {
  rule: ConsolidationRule | null;
  onClose: () => void;
  onSave: (data: Partial<ConsolidationRule>) => Promise<void>;
}

function RuleModal({ rule, onClose, onSave }: RuleModalProps) {
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [conditions, setConditions] = useState<ConsolidationRule['conditions']>(
    rule?.conditions || [{ field: 'entity_type', operator: 'equals', value: '' }],
  );
  const [conditionLogic, setConditionLogic] = useState(rule?.condition_logic || 'AND');
  const [actions, setActions] = useState<ConsolidationRule['actions']>(
    rule?.actions || [{ type: 'flag_for_review', params: {} }],
  );
  const [priority, setPriority] = useState(rule?.priority || 50);
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);

  const addCondition = () => {
    setConditions([...conditions, { field: 'amount', operator: 'greater_than', value: '' }]);
  };

  const removeCondition = (idx: number) => {
    setConditions(conditions.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx: number, field: string, value: any) => {
    setConditions(conditions.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const addAction = () => {
    setActions([...actions, { type: 'flag_for_review', params: {} }]);
  };

  const removeAction = (idx: number) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  const updateAction = (idx: number, type: string) => {
    setActions(actions.map((a, i) => (i === idx ? { ...a, type } : a)));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave({
        name,
        description,
        conditions,
        condition_logic: conditionLogic,
        actions,
        priority,
        is_active: isActive,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {rule ? 'Edit Rule' : 'Create New Rule'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Name & Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Rule Name</label>
              <input
                type="text"
                className="input-field w-full text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Eliminate IC Balances"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Priority (1-100)</label>
              <input
                type="range"
                min={1}
                max={100}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-400 mt-0.5">
                <span>Low</span>
                <span className="font-mono font-bold text-gray-700">{priority}</span>
                <span>High</span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this rule does..."
            />
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-500 uppercase">Conditions</label>
              <div className="flex items-center gap-2">
                <select
                  className="select-field text-xs w-20"
                  value={conditionLogic}
                  onChange={(e) => setConditionLogic(e.target.value)}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                <button
                  onClick={addCondition}
                  className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {conditions.map((cond, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="select-field text-xs flex-1"
                    value={cond.field}
                    onChange={(e) => updateCondition(idx, 'field', e.target.value)}
                  >
                    {CONDITION_FIELDS.map((f) => (
                      <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <select
                    className="select-field text-xs w-28"
                    value={cond.operator}
                    onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="input-field text-xs flex-1"
                    value={String(cond.value)}
                    onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                    placeholder="Value"
                  />
                  {conditions.length > 1 && (
                    <button
                      onClick={() => removeCondition(idx)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-500 uppercase">Actions</label>
              <button
                onClick={addAction}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {actions.map((action, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="select-field text-xs flex-1"
                    value={action.type}
                    onChange={(e) => updateAction(idx, e.target.value)}
                  >
                    {ACTION_TYPES.map((t) => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  {actions.length > 1 && (
                    <button
                      onClick={() => removeAction(idx)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsActive(!isActive)}
              className="flex items-center gap-2 text-sm"
            >
              {isActive ? (
                <ToggleRight className="w-6 h-6 text-green-600" />
              ) : (
                <ToggleLeft className="w-6 h-6 text-gray-400" />
              )}
              <span className={clsx('font-medium', isActive ? 'text-green-700' : 'text-gray-500')}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name}
            className="btn-primary text-sm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function RulesEngine() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluating, setEvaluating] = useState(false);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [rules, setRules] = useState<ConsolidationRule[]>([]);
  const [evaluationResult, setEvaluationResult] = useState<{
    matched: number;
    actions_taken: number;
    journal_entries: number;
  } | null>(null);

  // UI
  const [showModal, setShowModal] = useState(false);
  const [editRule, setEditRule] = useState<ConsolidationRule | null>(null);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load rules
  const loadRules = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await rulesApi.list();
      setRules(Array.isArray(res) ? res : []);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load rules');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Save rule
  const handleSave = async (data: Partial<ConsolidationRule>) => {
    try {
      if (editRule) {
        await rulesApi.update(editRule.id, data);
        addNotification({ type: 'success', title: 'Rule Updated', message: `Rule "${data.name}" has been updated.` });
      } else {
        await rulesApi.create(data);
        addNotification({ type: 'success', title: 'Rule Created', message: `Rule "${data.name}" has been created.` });
      }
      await loadRules();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Save Failed', message: apiErr.detail || 'Failed to save rule.' });
    }
  };

  // Delete rule
  const handleDelete = async (rule: ConsolidationRule) => {
    if (!confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await rulesApi.delete(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      addNotification({ type: 'success', title: 'Rule Deleted', message: `Rule "${rule.name}" has been deleted.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete rule.' });
    }
  };

  // Evaluate all rules
  const handleEvaluate = async () => {
    if (!selectedPeriod) return;
    setEvaluating(true);
    setEvaluationResult(null);
    try {
      const res = await rulesApi.evaluate(Number(selectedPeriod));
      setEvaluationResult(res);
      addNotification({
        type: 'success',
        title: 'Evaluation Complete',
        message: `${res.matched} rules matched, ${res.actions_taken} actions taken.`,
      });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Evaluation Failed', message: apiErr.detail || 'Rule evaluation failed.' });
    } finally {
      setEvaluating(false);
    }
  };

  // Skeleton loading
  if (isLoading && rules.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-32 h-5 mb-3" />
              <div className="skeleton w-full h-3 mb-2" />
              <div className="skeleton w-20 h-3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rules Engine</h1>
            <p className="text-sm text-gray-500 mt-0.5">Automate consolidation with configurable rules</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleEvaluate}
            disabled={evaluating || !selectedPeriod}
            className="btn-secondary text-sm"
          >
            {evaluating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Evaluate All
          </button>
          <button
            onClick={() => {
              setEditRule(null);
              setShowModal(true);
            }}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadRules} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Evaluation Result */}
      {evaluationResult && (
        <div className="card p-5 border-green-200 bg-green-50">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">Evaluation Complete</p>
              <p className="text-xs text-green-600 mt-0.5">
                {evaluationResult.matched} rules matched &middot;{' '}
                {evaluationResult.actions_taken} actions taken &middot;{' '}
                {evaluationResult.journal_entries} journal entries generated
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rule Cards */}
      {rules.length === 0 && !error ? (
        <div className="card py-16 text-center">
          <Settings2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No rules configured</h3>
          <p className="text-sm text-gray-500 mt-1">
            Create your first consolidation rule to automate common tasks.
          </p>
          <button
            onClick={() => {
              setEditRule(null);
              setShowModal(true);
            }}
            className="btn-primary mt-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={clsx(
                'card p-5 transition-all hover:shadow-card-hover',
                !rule.is_active && 'opacity-60',
              )}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">{rule.name}</h3>
                  <span className={clsx(
                    'badge text-xs',
                    rule.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500',
                  )}>
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <span className="badge bg-gray-100 text-gray-600 text-xs">
                  Priority: {rule.priority}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{rule.description || 'No description'}</p>

              {/* Conditions summary */}
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Conditions ({rule.condition_logic})</p>
                <div className="flex flex-wrap gap-1">
                  {rule.conditions.map((c, idx) => (
                    <span key={idx} className="badge bg-blue-50 text-blue-700 text-xs">
                      {c.field} {c.operator} {String(c.value)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions summary */}
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Actions</p>
                <div className="flex flex-wrap gap-1">
                  {rule.actions.map((a, idx) => (
                    <span key={idx} className="badge bg-purple-50 text-purple-700 text-xs">
                      {a.type.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>

              {rule.last_evaluated_at && (
                <p className="text-xs text-gray-400 mb-3">
                  Last evaluated: {new Date(rule.last_evaluated_at).toLocaleString()}
                </p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => {
                    setEditRule(rule);
                    setShowModal(true);
                  }}
                  className="text-xs font-medium text-primary-600 hover:text-primary-800"
                >
                  Edit
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => handleDelete(rule)}
                  className="text-xs font-medium text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <RuleModal
          rule={editRule}
          onClose={() => {
            setShowModal(false);
            setEditRule(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
